import styles from "./Volume.module.scss";
import { FC, useMemo } from "react";
import { Typography } from "@mui/material";
import { withStockStamp } from "@/components/stock-stamp-hoc/StockStampHOC";
import { classNames, nFormatter, thousandsSeparator } from "@/lib/utils";
import { CategoryBarChart } from "@/components/ortex-charts/Viz";
import type { CategoryChartOptions } from "@ortex-charts/react";
import { ortexColorState } from "@/lib/store";
import { useAtomValue } from "jotai";

interface PropsTypes {
    row?: any;
    volumeAvg: number | string;
    volumeTs: string[];
    overviewPage?: boolean;
    mobile?: boolean;
    obfuscated?: boolean;
}

const DAY_LABELS = ["-5d", "-4d", "-3d", "-2d", "-1d"];

/** Five daily volume columns with the period average marked. */
const Volume: FC<PropsTypes> = ({ volumeAvg, volumeTs, overviewPage, mobile, obfuscated = false }) => {
    const ortexColor = useAtomValue(ortexColorState);
    const data = useMemo(() => volumeTs?.map((value) => Number(value)) ?? [], [volumeTs]);
    const averageVolume = data.length > 0 ? Math.round(data.reduce((a, b) => a + b, 0) / data.length) : null;

    const options = useMemo<Partial<CategoryChartOptions>>(
        () => ({
            categories: DAY_LABELS.slice(-data.length),
            series: [{ key: "volume", title: "Volume", data, color: ortexColor }],
            legend: false,
            tooltip: !obfuscated,
            padding: 4,
            barPadding: 0.2,
            valueAxis: { visible: false, gridlines: false, format: { type: "volume", precision: 2 }, position: "left", min: 0 },
            categoryAxis: { visible: false, maxLabelWidth: 60 },
            valueLabelFormat: { type: "custom", formatter: (v: number) => thousandsSeparator(Math.round(v)) as unknown as string },
        }),
        [data, ortexColor, obfuscated]
    );

    return (
        <div className={classNames(styles.Volume, styles[overviewPage && "overview-page"])}>
            <Typography variant={mobile ? "h2" : "moduleContentTitle"} className={styles.volume}>
                {typeof volumeAvg === "number" ? nFormatter(volumeAvg, 2) : volumeAvg}
            </Typography>
            <div className={styles.BarChart}>
                {data.length > 0 && (
                    <>
                        <div className={styles.chart} title={averageVolume != null ? `Average ${thousandsSeparator(averageVolume)}` : undefined}>
                            <CategoryBarChart options={options} />
                        </div>
                        <div className={styles.labels}>
                            {DAY_LABELS.map((l) => (
                                <p key={l}>{l}</p>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default withStockStamp(Volume, "volumeAvg", { title: "Vol 3M Average" }, "Volume");
